import { CheckIcon, ChevronDownIcon, ChevronRightIcon } from '@persona-id/design-tokens/icons/16px';
import { useState } from 'react';

import {
  ChecksTableTD,
  ChecksTableTDContents,
} from 'dashboard/components/ChecksTable/ChecksTableRow';
import ChecksTableRowMetadata from 'dashboard/components/ChecksTable/ChecksTableRowMetadata';
import { computeMultipleChecksMetadata } from 'dashboard/components/ChecksTable/computeCheckMetadata';
import { getStatusForCheckRow as getStatusForCheckRowAamva } from 'dashboard/components/ChecksTable/computeCheckMetadata/aamvaAttributeChecks';
import { getStatusForCheckRow as getStatusForCheckRowBusinessWebsite } from 'dashboard/components/ChecksTable/computeCheckMetadata/businessWebsiteAttributeChecks';
import { getStatusForCheckRow as getStatusForCheckRowDatabase } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseAttributeChecks';
import { getStatusForCheckRow as getStatusForCheckRowDatabaseBusiness } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseBusinessAttributeChecks';
import { getStatusForCheckRow as getStatusForCheckRowDatabaseBusinessFootprint } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseBusinessFootprintAttributeChecks';
import { getStatusForCheckRow as getStatusForCheckRowDatabaseStandard } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseStandardAttributeChecks';
import { getStatusForCheckRow as getStatusForCheckRowDatabaseVat } from 'dashboard/components/ChecksTable/computeCheckMetadata/databaseVatNumberAttributeChecks';
import { TableTD, TableTR } from 'dashboard/layouts/TableLayout';
import { CheckStatus } from 'dashboard/models/check';
import {
  Verification,
  VerificationAamva,
  VerificationBusinessWebsite,
  VerificationDatabase,
  VerificationDatabaseBusiness,
  VerificationDatabaseBusinessFootprint,
  VerificationDatabaseStandard,
  VerificationDatabaseVatNumber,
} from 'dashboard/models/verification';
import Sticker from 'design-system/components/Sticker/Sticker';
import { ApiModel } from 'lib/interfaces/api';

interface ChecksTableRowMultipleChecksProps {
  verification: Verification;
  name?: string;
}

export const MULTIPLE_CHECKS_STATUS = 'multiple_checks';

// This component is used for rendering a row in the ChecksTable component that can represent
// multiple checks, e.g. AAMVA or legacy DBV with attribute comparison checks.
const ChecksTableRowMultipleChecks = ({
  verification,
  name = 'Attribute Checks',
}: ChecksTableRowMultipleChecksProps) => {
  // Note: When a row represents multiple checks, we expand it by default
  const [isExpanded, setIsExpanded] = useState(true);

  const checkMetadataProps = computeMultipleChecksMetadata(verification);

  return (
    <>
      <TableTR aria-expanded={isExpanded}>
        <ChecksTableTD hasBorderBottom={!isExpanded}>
          <ChecksTableTDContents hasLeftPadding={false}>
            {!isExpanded && <ChevronRightIcon onClick={() => setIsExpanded(true)} />}
            {isExpanded && <ChevronDownIcon onClick={() => setIsExpanded(false)} />}
            {(() => {
              let status;

              switch (verification.type) {
                case ApiModel.VerificationAamva:
                  status = getStatusForCheckRowAamva(verification as VerificationAamva);
                  break;
                case ApiModel.VerificationBusinessWebsite:
                  status = getStatusForCheckRowBusinessWebsite(
                    verification as VerificationBusinessWebsite,
                  );
                  break;
                case ApiModel.VerificationDatabase:
                  status = getStatusForCheckRowDatabase(verification as VerificationDatabase);
                  break;
                case ApiModel.VerificationDatabaseBusiness:
                  status = getStatusForCheckRowDatabaseBusiness(
                    verification as VerificationDatabaseBusiness,
                  );
                  break;
                case ApiModel.VerificationDatabaseBusinessFootprint:
                  status = getStatusForCheckRowDatabaseBusinessFootprint(
                    verification as VerificationDatabaseBusinessFootprint,
                  );
                  break;
                case ApiModel.VerificationDatabaseStandard:
                  status = getStatusForCheckRowDatabaseStandard(
                    verification as VerificationDatabaseStandard,
                  );
                  break;
                case ApiModel.VerificationDatabaseVatNumber:
                  status = getStatusForCheckRowDatabaseVat(
                    verification as VerificationDatabaseVatNumber,
                  );
                  break;
                default:
                  status = MULTIPLE_CHECKS_STATUS;
              }

              switch (status) {
                case CheckStatus.Passed:
                  return <Sticker variant='success'>Passed</Sticker>;
                case CheckStatus.Failed:
                  return <Sticker variant='danger'>Failed</Sticker>;
                case CheckStatus.NotApplicable:
                  return <Sticker variant='basic'>N/A</Sticker>;
                case MULTIPLE_CHECKS_STATUS:
                  return <Sticker variant='basic'>Multiple checks</Sticker>;
                default:
                  return <Sticker variant='basic'>Multiple checks</Sticker>;
              }
            })()}
          </ChecksTableTDContents>
        </ChecksTableTD>
        <ChecksTableTD hasBorderBottom={!isExpanded}>
          <ChecksTableTDContents>{name}</ChecksTableTDContents>
        </ChecksTableTD>
        <ChecksTableTD hasBorderBottom={!isExpanded}>
          {/* Note: When a row represents multiple checks, it's always type: Fraud */}
          Fraud
        </ChecksTableTD>
        <ChecksTableTD hasBorderBottom={!isExpanded}>
          {/* Note: When a row represents multiple checks, it's always marked as Required */}
          <CheckIcon data-test={`checks-table-v2-row__${verification.type}-required-icon`} />
        </ChecksTableTD>
      </TableTR>
      {isExpanded && (
        <TableTR disableHoverBackground={true}>
          <TableTD colSpan={4}>
            <ChecksTableRowMetadata id={verification.type} {...checkMetadataProps} />
          </TableTD>
        </TableTR>
      )}
    </>
  );
};

export default ChecksTableRowMultipleChecks;
